import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle } from 'lucide-react';
import { BrandLogo } from '@/components/common/BrandLogo';
import { mainNavigation } from '@/data/navigation';
import { siteConfig } from '@/data/site';
import { useScrollPosition, useBodyScrollLock } from '@/hooks/useScrollPosition';
import { buildGeneralWhatsAppMessage, buildWhatsAppUrl, cn } from '@/utils';
import { trackEvent } from '@/services/analytics';

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const scrolled = useScrollPosition(60);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useBodyScrollLock(menuOpen);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const whatsappPhone = siteConfig.contact.whatsapp;
  const whatsappUrl = whatsappPhone
    ? buildWhatsAppUrl(whatsappPhone, buildGeneralWhatsAppMessage())
    : undefined;

  const handleWhatsAppClick = () => {
    void trackEvent('whatsapp_clicked', { context: 'header' });
  };

  return (
    <>
      <a href="#main-content" className="skip-link">
        Saltar al contenido
      </a>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all duration-300',
          transparent
            ? 'bg-transparent'
            : 'bg-semantic-surface/95 shadow-header backdrop-blur-sm',
        )}
      >
        <div className="container-content flex h-16 items-center justify-between md:h-20">
          <Link to="/" className="relative z-10 shrink-0" aria-label="UGA Bienes Raíces — Inicio">
            <BrandLogo variant={transparent ? 'light' : 'dark'} />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Navegación principal">
            {mainNavigation.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    transparent
                      ? isActive
                        ? 'text-gold-light'
                        : 'text-semantic-text-on-dark/90 hover:text-gold-light'
                      : isActive
                        ? 'text-semantic-accent'
                        : 'text-semantic-text-primary hover:text-semantic-accent',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {whatsappUrl ? (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp hidden text-sm md:inline-flex"
                onClick={handleWhatsAppClick}
                aria-label="Contactar por WhatsApp"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                Hablar con un asesor
              </a>
            ) : (
              <Link to="/contacto" className="btn-primary hidden text-sm md:inline-flex">
                Contacto
              </Link>
            )}

            <button
              type="button"
              className={cn(
                'inline-flex h-11 w-11 items-center justify-center rounded-lg lg:hidden',
                transparent ? 'text-semantic-text-on-dark' : 'text-semantic-text-primary',
              )}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            ref={menuRef}
            id="mobile-menu"
            className="fixed inset-0 top-16 z-40 bg-semantic-surface lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Menú de navegación"
          >
            <nav className="container-content flex flex-col gap-1 py-6" aria-label="Navegación móvil">
              {mainNavigation.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'rounded-lg px-4 py-3 text-base font-medium',
                      isActive
                        ? 'bg-ivory-secondary text-semantic-accent'
                        : 'text-semantic-text-primary hover:bg-ivory-secondary',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="mt-4 border-t border-semantic-border pt-4">
                {whatsappUrl ? (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp w-full"
                    onClick={handleWhatsAppClick}
                  >
                    <MessageCircle className="h-5 w-5" aria-hidden="true" />
                    Hablar con un asesor
                  </a>
                ) : (
                  <Link to="/contacto" className="btn-primary w-full">
                    Contacto
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
